import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Brain, Users, MessageCircle, Calendar, Zap, Wifi } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "../data/images";
import CTABand from "../components/shared/CTABand";

function RevealSection({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

export default function ConnectPage() {
  return (
    <div data-testid="connect-page">
      <section className="page-hero" style={{ backgroundColor: "var(--surface)" }} data-testid="connect-hero">
        <div className="container-xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="overline-label mb-4">Networking & Connection</div>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-white mb-6"
              style={{ fontFamily: "var(--font-heading)" }}>Connections by design.</h1>
            <p className="text-lg max-w-2xl" style={{ color: "var(--text-muted)" }}>
              The connections at Core AI Summit aren't accidental. Every networking touchpoint is engineered to put the right people in front of each other at the right time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* AI Concierge — COREy */}
      <section id="concierge" className="section-py" data-testid="concierge-section">
        <div className="container-xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <RevealSection>
              <div>
                <div className="overline-label mb-4">Introducing COREy</div>
                <h2 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-6"
                  style={{ fontFamily: "var(--font-heading)" }}>
                  Your AI concierge.<br />
                  <span style={{ color: "var(--cyan)" }}>Powered by intelligence.</span>
                </h2>
                <p className="text-base leading-relaxed mb-6" style={{ color: "var(--text-muted)" }}>
                  COREy is the AI personality behind Core AI Summit — your intelligent guide for the event. Ask in plain language and get curated answers: which sessions to attend, who you should meet, which exhibitors match your needs.
                </p>
                <div className="space-y-4 mb-8">
                  {[
                    { title: "Natural language queries", desc: "Ask 'Who should I meet if I'm scaling AI in healthcare?' and get a curated list." },
                    { title: "Session recommendations", desc: "COREy analyzes your profile and goals to suggest the right sessions across all tracks." },
                    { title: "Exhibitor matching", desc: "Tell COREy your challenges, and it points you to the exhibitors most relevant to your context." },
                    { title: "Human backup", desc: "When COREy can't answer, a real human concierge is standing by." },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2" style={{ backgroundColor: "var(--cyan)" }} />
                      <div>
                        <div className="text-sm font-semibold text-white">{item.title}</div>
                        <div className="text-sm" style={{ color: "var(--text-muted)" }}>{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs"
                  style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.2)", color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>
                  <Brain size={12} /> In development — launching with the event app
                </div>
              </div>
            </RevealSection>

            <RevealSection delay={0.15}>
              <div className="relative">
                <div className="p-8 rounded-2xl" style={{ background: "var(--surface)", border: "1px solid rgba(0,212,255,0.2)" }}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: "linear-gradient(135deg, var(--cyan), var(--violet))" }}>
                      <Brain size={18} className="text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-white" style={{ fontFamily: "var(--font-heading)" }}>COREy</div>
                      <div className="text-xs" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>AI Concierge · Core AI Summit</div>
                    </div>
                    <div className="ml-auto w-2 h-2 rounded-full bg-green-400" />
                  </div>
                  
                  <div className="space-y-4">
                    {[
                      { type: "user", msg: "Who should I meet if I want to adopt AI in my manufacturing business?" },
                      { type: "core", msg: "Great question. Based on your profile, I'd recommend connecting with 3 exhibitors in the Industrial AI category, and 12 attendees who've implemented AI in operations. I've also queued up the 'Automating Manufacturing with AI' session at 2pm on Day 1. Want me to book a 1:1 with the session speaker?" },
                    ].map((item, i) => (
                      <div key={i} className={`flex ${item.type === "user" ? "justify-end" : "justify-start"}`}>
                        <div className="max-w-xs px-4 py-3 rounded-2xl text-sm"
                          style={{
                            background: item.type === "user" ? "rgba(0,212,255,0.15)" : "var(--bg)",
                            border: item.type === "user" ? "1px solid rgba(0,212,255,0.3)" : "1px solid var(--border)",
                            color: "var(--text)",
                          }}>
                          {item.msg}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 flex items-center gap-3 pt-4" style={{ borderTop: "1px solid var(--border)" }}>
                    <input placeholder="Ask COREy anything..." className="input-dark text-sm py-2.5" disabled
                      style={{ background: "var(--bg)", opacity: 0.6 }} />
                    <button className="btn-primary py-2.5 px-4" disabled style={{ opacity: 0.5 }}>
                      <ArrowRight size={14} />
                    </button>
                  </div>
                  <p className="text-xs mt-3 text-center" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                    Available in the event app — launching 2027
                  </p>
                </div>
              </div>
            </RevealSection>
          </div>
        </div>
      </section>

      {/* Networking Features */}
      <section className="section-py" style={{ backgroundColor: "var(--surface)" }} data-testid="networking-features-section">
        <div className="container-xl">
          <RevealSection>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <div className="overline-label mb-4">Networking Features</div>
              <h2 className="text-4xl font-black tracking-tight text-white" style={{ fontFamily: "var(--font-heading)" }}>
                Every way to connect — engineered.
              </h2>
            </div>
          </RevealSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Calendar,
                title: "Curated 1:1 Meetings",
                desc: "AI-matched appointment scheduling with people you actually want to meet. Book up to 10 meetings through the event app before you arrive.",
              },
              {
                icon: MessageCircle,
                title: "Roundtable Discussions",
                desc: "Intimate, off-the-record peer conversations in groups of 10-15. Tackle shared challenges without the pressure of a main stage.",
              },
              {
                icon: Users,
                title: "Networking Receptions",
                desc: "Daily happy hours and dedicated evening receptions — structured enough to make connections, relaxed enough to be human.",
              },
              {
                icon: Wifi,
                title: "Smart Badges",
                desc: "Digital credentials that track your preferences and unlock personalized experiences, recommendations, and connections throughout the event.",
              },
              {
                icon: Zap,
                title: "Industry Meetups",
                desc: "Informal gatherings by industry vertical — healthcare, finance, manufacturing, and more. Find your people in the first hour.",
              },
              {
                icon: Brain,
                title: "EventOS App",
                desc: "The Core AI event app — your agenda, meetings, networking, and COREy AI concierge, all in one place. Coming 2027.",
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <RevealSection key={item.title} delay={i * 0.08}>
                  <div className="card card-glow p-6 h-full" data-testid={`networking-feature-${i}`}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                      style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.15)" }}>
                      <Icon size={18} style={{ color: "var(--cyan)" }} />
                    </div>
                    <h4 className="font-bold text-white mb-2" style={{ fontFamily: "var(--font-heading)" }}>{item.title}</h4>
                    <p className="text-sm" style={{ color: "var(--text-muted)" }}>{item.desc}</p>
                  </div>
                </RevealSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Networking image */}
      <section className="section-py" data-testid="networking-image-section">
        <div className="container-xl">
          <RevealSection>
            <div className="relative rounded-2xl overflow-hidden">
              <img src={IMAGES.networking} alt="Networking at Core AI Summit" className="w-full h-72 md:h-96 object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(9,13,22,0.9) 0%, rgba(9,13,22,0.4) 60%, transparent 100%)" }} />
              <div className="absolute inset-0 flex items-center">
                <div className="container-xl">
                  <div className="max-w-md">
                    <div className="overline-label mb-3">The Real ROI of Core AI</div>
                    <h3 className="text-3xl font-black text-white mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                      The best meeting you'll have all year — happens here.
                    </h3>
                    <p className="text-sm mb-5" style={{ color: "var(--text-muted)" }}>
                      The connections you make at Core AI Summit don't end when the event does. They become partnerships, customers, mentors, and collaborators.
                    </p>
                    <Link to="/attend" className="btn-primary">Join the Event <ArrowRight size={14} /></Link>
                  </div>
                </div>
              </div>
            </div>
          </RevealSection>
        </div>
      </section>

      <CTABand
        title="Start Connecting at Core AI 2027"
        subtitle="Join the priority list and be first to unlock the networking app and 1:1 meeting system."
        primaryLabel="Join Priority List"
      />
    </div>
  );
}
